import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Heart, MessageCircle, Send, Loader2, Trash2, ArrowLeft, ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getPostById, getComments, addComment, deleteComment, toggleLikePost, deletePost } from '../lib/services/feedService';
import type { GetFeedResult, CommentWithAuthor, PostRow } from '../types/database';
import SEO from '../components/SEO';
import CommentItem from '../components/feed/CommentItem';
import toast from 'react-hot-toast';

// Reutilizar helper do PostItem ou mover para utils
const getYouTubeEmbedUrl = (url: string | null): string | null => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    let videoId: string | null = null;
    if (urlObj.hostname === 'www.youtube.com' || urlObj.hostname === 'youtube.com') {
      if (urlObj.pathname === '/watch') videoId = urlObj.searchParams.get('v');
      else if (urlObj.pathname.startsWith('/embed/')) return url;
    } else if (urlObj.hostname === 'youtu.be') videoId = urlObj.pathname.substring(1);
    if (videoId) return `https://www.youtube.com/embed/${videoId.split('&')[0]}`;
    console.warn('Could not extract YouTube video ID from URL:', url);
    return null;
  } catch (e) {
    console.error('Invalid URL for video embedding:', url, e);
    return null;
  }
};

const PostPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [post, setPost] = useState<GetFeedResult | null>(null);
  const [loadingPost, setLoadingPost] = useState(true);
  const [postError, setPostError] = useState<string | null>(null);

  const [comments, setComments] = useState<CommentWithAuthor[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  const [newComment, setNewComment] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [isModeratingComment, setIsModeratingComment] = useState(false);

  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isLiking, setIsLiking] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);

  // Fetch Post Data
  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) {
        setPostError('ID do post inválido.');
        setLoadingPost(false);
        return;
      }
      setLoadingPost(true);
      setPostError(null);
      try {
        const fetchedPost = await getPostById(postId, user?.id || null);
        if (fetchedPost) {
          setPost(fetchedPost);
          setIsLiked(fetchedPost.user_liked || false);
          setLikeCount(fetchedPost.like_count || 0);
          // Fetch comments only after post is loaded
          fetchCommentsForPost(postId);
        } else {
          setPostError('Post não encontrado.');
          setPost(null);
        }
      } catch (error) {
        console.error('Erro ao buscar post:', error);
        setPostError('Falha ao carregar o post.');
        setPost(null);
      } finally {
        setLoadingPost(false);
      }
    };
    fetchPost();
  }, [postId, user?.id]); // Depend on postId and user ID

  // Fetch Comments
  const fetchCommentsForPost = useCallback(async (id: string) => {
    setLoadingComments(true);
    setCommentError(null);
    try {
      const fetchedComments = await getComments(id);
      setComments(fetchedComments || []);
    } catch (error) {
      console.error('Erro ao buscar comentários:', error);
      setCommentError('Não foi possível carregar os comentários.');
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, []); // useCallback without dependencies as it uses postId from effect

  // Handle Add Comment
  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newComment.trim() || isPostingComment || isModeratingComment || !postId) return;

    setIsPostingComment(true);
    setIsModeratingComment(true);
    toast.loading('Verificando seu comentário...', { id: 'postpage-comment-moderation-toast' });

    try {
      // 1. Moderação de Texto
      const { data: textModerationResult, error: textModerationError } = await supabase.functions.invoke('moderate-text', {
        body: { textContent: newComment.trim() }
      });

      toast.dismiss('postpage-comment-moderation-toast');
      setIsModeratingComment(false);

      if (textModerationError) {
        console.warn('[PostPage] Erro crítico ao invocar a função de moderação de texto do comentário:', textModerationError);
        toast.error(textModerationError.message || 'Falha crítica ao verificar o comentário.', { duration: 7000 });
        setIsPostingComment(false);
        return;
      }

      if (textModerationResult && !textModerationResult.success) {
        const detailedErrorMessage = textModerationResult.error || 'Conteúdo do comentário considerado impróprio.';
        console.warn(`[PostPage] Moderação de texto do comentário falhou: ${detailedErrorMessage}`);
        toast.error(detailedErrorMessage, { duration: 7000 });
        setIsPostingComment(false);
        return;
      }
      
      // if (textModerationResult && textModerationResult.success) {
      //   toast.success(textModerationResult.message || 'Comentário verificado!', { duration: 2000 });
      // }

      // 2. Adicionar Comentário
      const payload = { post_id: postId, user_id: user.id, content: newComment.trim() };
      const createdCommentPostRow = await addComment(payload);
      const optimisticComment: CommentWithAuthor = {
        id: createdCommentPostRow.id,
        user_id: user.id,
        content: newComment.trim(),
        created_at: new Date().toISOString(),
        parent_post_id: postId,
        author_profile: {
          username: user.username || null,
          avatar_url: user.avatar_url || null,
          full_name: user.full_name || null,
        }
      };
      setComments(prev => [...prev, optimisticComment]);
      setNewComment('');
      setPost(prev => prev ? ({ ...prev, comment_count: (prev.comment_count || 0) + 1 }) : null);
      toast.success('Comentário adicionado!');
    } catch (error) {
      console.error('Erro ao adicionar comentário ou durante moderação:', error);
      toast.error('Não foi possível adicionar seu comentário.');
      toast.dismiss('postpage-comment-moderation-toast'); // Garante que o toast de loading seja removido em caso de erro
    } finally {
      setIsPostingComment(false);
      setIsModeratingComment(false);
    }
  };

  // Handle Delete Comment
  const handleCommentDeleted = (deletedCommentId: string) => {
    setComments(prev => prev.filter(comment => comment.id !== deletedCommentId));
    // Optionally update post's comment_count locally if needed
    setPost(prev => prev ? ({ ...prev, comment_count: Math.max(0, (prev.comment_count || 0) - 1) }) : null);
  };

  // Handle Like/Unlike Post
  const handleToggleLike = async () => {
    if (!user || !post || isLiking) {
      if(!user) toast.error('Você precisa estar logado para curtir.');
      return;
    }
    
    const originalLiked = isLiked;
    const originalCount = likeCount;
    const authorId = post.user_id;

    // Optimistic update
    setIsLiked(!originalLiked);
    setLikeCount(prev => originalLiked ? Math.max(0, prev - 1) : prev + 1);
    setIsLiking(true);

    try {
      const result = await toggleLikePost(post.id, user.id, authorId);
      
      setIsLiked(result.liked);
      setLikeCount(result.likeCount >= 0 ? result.likeCount : originalCount);

    } catch (error) {
      console.error('Erro ao processar like:', error);
      toast.error('Não foi possível processar sua curtida.');
      // Revert optimistic update on error
      setIsLiked(originalLiked);
      setLikeCount(originalCount);
    } finally {
      setIsLiking(false);
    }
  };

  // Handle Delete Post
  const handleDeletePost = async () => {
    if (!user || !post || (post.user_id !== user.id && !isAdmin)) return;

    if (!window.confirm('Tem certeza que deseja excluir este post e todos os seus comentários?')) {
      return;
    }
    setIsDeletingPost(true);
    try {
      const success = await deletePost(post.id);
      if (success) {
        toast.success('Post excluído com sucesso!');
        navigate('/feed'); // Redirect to feed page
      } else {
        toast.error('Não foi possível excluir o post.');
        setIsDeletingPost(false);
      }
    } catch (error) {
      console.error('Erro ao excluir post:', error);
      toast.error('Erro ao excluir o post.');
      setIsDeletingPost(false);
    }
  };

  // Formatted Date for Post
  const formattedDate = useMemo(() => {
    if (!post?.created_at) return '';
    try {
      const date = new Date(post.created_at);
      return isNaN(date.getTime()) ? 'Data inválida' : formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
    } catch (error) { return 'Data indisponível'; }
  }, [post?.created_at]);

  const embedUrl = useMemo(() => getYouTubeEmbedUrl(post?.video_url || null), [post?.video_url]);
  const isAuthor = user && post && user.id === post.user_id;

  // Loading State
  if (loadingPost) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-indigo-600 dark:text-indigo-400" />
      </div>
    );
  }

  // Error State
  if (postError) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-8 text-center">
        <p className="text-red-600 bg-red-100 p-4 rounded-md dark:bg-red-900/30 dark:text-red-400">{postError}</p>
        <Link to="/feed" className="mt-4 inline-block text-indigo-600 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300">
          Voltar para o Feed
        </Link>
      </div>
    );
  }

  // Post Not Found (handled by postError currently)
  if (!post) {
     return (
       <div className="container mx-auto max-w-3xl px-4 py-8 text-center">
         <p className="text-gray-600 bg-gray-100 p-4 rounded-md dark:bg-gray-700 dark:text-gray-300">Post não encontrado.</p>
          <Link to="/feed" className="mt-4 inline-block text-indigo-600 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300">
            Voltar para o Feed
          </Link>
       </div>
     );
  }


  // Render Post Details and Comments
  return (
    <>
      <SEO title={`Post de ${post.author_username || 'Usuário'}`} description={post.content_text?.substring(0, 150) || 'Veja este post no Boardverso'} />
      <div className="container mx-auto max-w-3xl px-4 py-8">
         {/* Back Button */}
         <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100">
             <ArrowLeft size={16} className="mr-1" />
             Voltar
         </button>

        <article className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
          {/* Post Header */}
          <div className="p-4 sm:p-6 flex items-start">
            {post.author_username ? (
              <Link to={`/u/${post.author_username}`} className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xl overflow-hidden dark:bg-indigo-500 dark:text-indigo-100">
                  {post.author_avatar_url ? (
                    <img src={post.author_avatar_url} alt={post.author_username} className="w-full h-full object-cover" />
                  ) : (post.author_username || 'U').charAt(0).toUpperCase()}
                </div>
              </Link>
            ) : (
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-xl overflow-hidden dark:bg-gray-700 dark:text-gray-300">
                  {(post.author_username || 'U').charAt(0).toUpperCase()}
                </div>
              </div>
            )}
            <div className="ml-4 flex-1">
              <div className="flex items-center justify-between">
                <div>
                  {post.author_username ? (
                    <Link to={`/u/${post.author_username}`} className="font-semibold text-gray-900 hover:text-indigo-600 dark:text-gray-100 dark:hover:text-indigo-400">
                      {post.author_username}
                    </Link>
                  ) : (
                    <span className="font-semibold text-gray-900 dark:text-gray-100">Usuário</span>
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400">{formattedDate}</p>
                </div>
                {(isAuthor || isAdmin) && (
                  <button
                    onClick={handleDeletePost}
                    disabled={isDeletingPost}
                    className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-gray-700"
                    aria-label="Excluir post"
                  >
                    {isDeletingPost ? <Loader2 size={18} className="animate-spin dark:text-gray-300" /> : <Trash2 size={18} />}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Post Content */}
          <div className="px-4 sm:px-6 pb-4">
            {post.content_text && (
              <div className="text-gray-800 whitespace-pre-line mb-4 text-base dark:text-gray-200">
                {post.content_text}
              </div>
            )}
            {post.image_url && (
              <div className="mb-4 rounded-lg overflow-hidden border border-gray-100 dark:border-gray-700">
                <img src={post.image_url} alt="Conteúdo do post" className="w-full object-contain max-h-[60vh]" loading="lazy" />
              </div>
            )}
            {embedUrl && (
              <div className="mb-4 rounded-lg overflow-hidden aspect-video border border-gray-100 dark:border-gray-700">
                <iframe src={embedUrl} title="Vídeo do post" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
              </div>
            )}
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    {post.tags.map((tag) => {
                        const displayValue = tag.tag_type === 'game' ? (tag.game_name || tag.game_id || '') : tag.tag_name || '';
                        if (!displayValue) return null;
                        const tagKey = `${tag.tag_type}-${tag.id || displayValue}`;
                        let tagClass = '';
                        let emoji = '';
                        switch (tag.tag_type) {
                            case 'game': tagClass = 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200 dark:bg-indigo-700 dark:text-indigo-200 dark:hover:bg-indigo-600'; emoji = '🎮'; break;
                            case 'category': tagClass = 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-700 dark:text-blue-200 dark:hover:bg-blue-600'; emoji = '📋'; break;
                            case 'mechanic': tagClass = 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-700 dark:text-green-200 dark:hover:bg-green-600'; emoji = '⚙️'; break;
                            case 'theme': tagClass = 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-600 dark:text-yellow-100 dark:hover:bg-yellow-500'; emoji = '🎭'; break;
                            default: tagClass = 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-100 dark:hover:bg-gray-500'; emoji = '🏷️';
                        }
                        // Link para a página de busca/filtro (se implementado) ou apenas exibir
                        return (
                            <span key={tagKey} className={`inline-flex items-center px-3 py-1 mr-2 mb-2 rounded-full text-sm font-medium ${tagClass}`}>
                                <span className="mr-1.5">{emoji}</span>
                                {displayValue}
                            </span>
                        );
                    })}
                </div>
            )}
          </div>

          {/* Post Actions */}
          <div className="px-4 sm:px-6 py-3 border-t border-gray-100 flex items-center space-x-6 dark:bg-gray-750 dark:border-gray-700">
            <button
              onClick={handleToggleLike}
              className={`flex items-center text-base font-medium ${isLiked ? 'text-pink-600 hover:text-pink-700 dark:text-pink-400 dark:hover:text-pink-300' : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100'} transition-colors ${isLiking ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label={isLiked ? 'Descurtir' : 'Curtir'}
              disabled={!user || isLiking}
            >
              {isLiking ? (
                  <Loader2 size={20} className="animate-spin mr-1.5 dark:text-gray-300" />
              ) : (
                  <Heart size={20} className={`mr-1.5 ${isLiked ? 'fill-current' : ''}`} />
              )}
              <span className="dark:text-gray-200">{likeCount} {likeCount === 1 ? 'Curtida' : 'Curtidas'}</span>
            </button>
            <div className="flex items-center text-base text-gray-600 dark:text-gray-300">
              <MessageCircle size={20} className="mr-1.5" />
              <span className="dark:text-gray-200">{post.comment_count || 0} {post.comment_count === 1 ? 'Comentário' : 'Comentários'}</span>
            </div>
          </div>

          {/* Comments Section */}
          <div className="border-t border-gray-200 px-4 sm:px-6 py-6 dark:bg-gray-750 dark:border-gray-700">
            <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">Comentários</h2>
            {/* Add Comment Form */}
            {user ? (
              <form onSubmit={handleAddComment} className="flex items-start mb-6">
                <img
                  src={user.avatar_url || '/default-avatar.png'} // Add a default avatar path
                  alt="Seu avatar"
                  className="w-9 h-9 rounded-full mr-3 flex-shrink-0 bg-gray-200 dark:bg-gray-600"
                />
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Escreva seu comentário..."
                  className="flex-grow p-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none text-sm dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400 dark:focus:ring-indigo-400 dark:focus:border-indigo-400"
                  rows={2}
                  disabled={isPostingComment || isModeratingComment}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || isPostingComment || isModeratingComment}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-r-md hover:bg-indigo-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:ring-offset-0 disabled:opacity-50 flex items-center justify-center self-stretch dark:bg-indigo-500 dark:hover:bg-indigo-600 dark:focus:ring-indigo-400"
                >
                  {isModeratingComment ? (
                    <ShieldAlert size={18} className="animate-pulse mr-1" />
                  ) : isPostingComment ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Send size={18} />
                  )}
                  {isModeratingComment ? 'Verificando...' : isPostingComment ? 'Enviando...' : 'Enviar'}
                </button>
              </form>
            ) : (
              <p className="text-center text-gray-600 mb-6 text-sm dark:text-gray-300">
                <Link to="/signin" className="text-indigo-600 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300">Faça login</Link> para comentar.
              </p>
            )}

            {/* Comments List */}
            {loadingComments ? (
              <div className="text-center py-6">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto dark:text-indigo-400" />
              </div>
            ) : commentError ? (
              <p className="text-center text-red-500 bg-red-50 p-4 rounded-md dark:bg-red-900/30 dark:text-red-400">{commentError}</p>
            ) : comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map(comment => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    onCommentDeleted={handleCommentDeleted}
                  />
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-6 dark:text-gray-400">Seja o primeiro a comentar!</p>
            )}
          </div>
        </article>
      </div>
    </>
  );
};

export default PostPage; 